// Define interfaces for type safety
interface Requirements {
  courseCompletion: boolean;
  minimumScore: number;
  requiredAssignments: string[];
  requiredQuizzes: string[];
  practicalProjects: number;
  capstoneProject?: boolean;
  awsCertificationPrep?: boolean;
}

interface CertificateDetails {
  badgeDesign: string;
  borderColor: string;
  accentColor: string;
  logoUrl: string;
}

interface Certification {
  id: string;
  name: string;
  description: string;
  courseId: string;
  courseName: string;
  status: 'active' | 'draft';
  templateId: string;
  requirements: Requirements;
  validityPeriod: number;
  issuingOrganization: string;
  certificateDetails: CertificateDetails;
  totalIssued: number;
  totalCompleted: number;
  averageCompletionTime: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

interface DesignElements {
  layout: 'landscape' | 'portrait';
  headerHeight: string;
  footerHeight: string;
  backgroundColor: string;
  borderWidth: number;
  borderStyle: 'solid' | 'double' | 'none';
  fontFamily: string;
  titleFontSize: string;
  bodyFontSize: string;
  signatureFontSize: string;
}

interface Placeholders {
  recipientName: string;
  certificationName: string;
  completionDate: string;
  issuingOrganization: string;
  certificateNumber: string;
  signatoryName: string;
  signatoryTitle: string;
}

interface CustomField {
  name: string;
  label: string;
  type: 'text' | 'number' | 'textarea';
  required: boolean;
}

interface Template {
  id: string;
  name: string;
  description: string;
  category: string;
  designElements: DesignElements;
  placeholders: Placeholders;
  customFields: CustomField[];
  usageCount: number;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

interface IssuedCertificate {
  id: string;
  certificationId: string;
  certificationName: string;
  userId: string;
  recipientName: string;
  recipientEmail: string;
  certificateNumber: string;
  issuedDate: string;
  completedDate: string;
  expiryDate: string;
  status: 'active' | 'revoked';
  finalScore: number;
  completionTime: number;
  downloadUrl: string;
  verificationUrl: string;
  shareUrl: string;
  blockchainHash: string;
  metadata: {
    templateUsed: string;
    issuerSignature: string;
    issuerTitle: string;
    organizationSeal: string;
    projects?: string[];
    capstoneProject?: string;
    technologies?: string[];
  };
}

interface Progress {
  id: string;
  certificationId: string;
  userId: string;
  userName: string;
  userEmail: string;
  status: 'in_progress' | 'completed';
  overallProgress: number;
  requirements: {
    courseCompletion: {
      required: boolean;
      completed: boolean;
      completedAt?: string;
      progress?: number;
    };
    minimumScore: {
      required: number;
      currentScore: number;
      achieved: boolean;
    };
    requiredAssignments: {
      required: string[];
      completed: string[];
      pending: string[];
    };
    requiredQuizzes: {
      required: string[];
      completed: string[];
      scores?: { [key: string]: number };
      pending?: string[];
    };
    practicalProjects: {
      required: number;
      completed: number;
      submitted: string[];
    };
  };
  estimatedCompletion: string;
  createdAt: string;
  updatedAt: string;
}

interface Analytics {
  certificationId: string;
  totalEnrolled: number;
  totalCompleted: number;
  completionRate: number;
  averageCompletionTime: number;
  certificatesIssued: number;
  certificatesActive: number;
  certificatesRevoked: number;
  averageScore: number;
  monthlyStats: {
    month: string;
    enrolled: number;
    completed: number;
    issued: number;
  }[];
  topScores: {
    userId: string;
    name: string;
    score: number;
  }[];
  difficultyBreakdown: {
    requirements: number;
    quizzes: number;
    assignments: number;
    projects: number;
  };
}

interface Category {
  id: string;
  name: string;
  description: string;
  count: number;
  color: string;
}

interface MockCertificationData {
  certifications: Certification[];
  templates: Template[];
  issuedCertificates: IssuedCertificate[];
  progress: Progress[];
  analytics: Analytics[];
  categories: Category[];
}

export const mockCertificationData: MockCertificationData = {
  certifications: [
    {
      id: 'cert_1',
      name: 'JavaScript Fundamentals Certificate',
      description: 'Certificate demonstrating proficiency in JavaScript programming fundamentals including variables, functions, objects, and ES6+ features.',
      courseId: '1',
      courseName: 'Advanced JavaScript Programming',
      status: 'active',
      templateId: 'template_1',
      requirements: {
        courseCompletion: true,
        minimumScore: 80,
        requiredAssignments: ['assignment_1', 'assignment_2'],
        requiredQuizzes: ['quiz_1'],
        practicalProjects: 1,
      },
      validityPeriod: 730, // days (2 years)
      issuingOrganization: 'Skillzen Academy',
      certificateDetails: {
        badgeDesign: 'javascript-badge.svg',
        borderColor: '#f7df1e',
        accentColor: '#323330',
        logoUrl: 'https://skillzen.com/logos/js-cert.png',
      },
      totalIssued: 156,
      totalCompleted: 142,
      averageCompletionTime: 28, // days
      createdAt: '2024-10-15T08:00:00Z',
      updatedAt: '2024-12-01T10:30:00Z',
      createdBy: 'instructor@skillzen.com',
    },
    {
      id: 'cert_2',
      name: 'React Development Certificate',
      description: 'Comprehensive certification in React development covering components, hooks, state management, and advanced patterns.',
      courseId: '2',
      courseName: 'React Development Masterclass',
      status: 'active',
      templateId: 'template_2',
      requirements: {
        courseCompletion: true,
        minimumScore: 85,
        requiredAssignments: ['assignment_3', 'assignment_4'],
        requiredQuizzes: ['quiz_2'],
        practicalProjects: 2,
      },
      validityPeriod: 1095, // days (3 years)
      issuingOrganization: 'Skillzen Academy',
      certificateDetails: {
        badgeDesign: 'react-badge.svg',
        borderColor: '#61dafb',
        accentColor: '#20232a',
        logoUrl: 'https://skillzen.com/logos/react-cert.png',
      },
      totalIssued: 89,
      totalCompleted: 76,
      averageCompletionTime: 42, // days
      createdAt: '2024-11-01T09:00:00Z',
      updatedAt: '2024-12-05T14:15:00Z',
      createdBy: 'instructor@skillzen.com',
    },
    {
      id: 'cert_3',
      name: 'Full Stack Developer Certificate',
      description: 'Advanced certification for full-stack development covering frontend, backend, databases, and deployment.',
      courseId: '3',
      courseName: 'Full Stack Development Bootcamp',
      status: 'active',
      templateId: 'template_3',
      requirements: {
        courseCompletion: true,
        minimumScore: 90,
        requiredAssignments: ['assignment_5', 'assignment_6', 'assignment_7'],
        requiredQuizzes: ['quiz_3', 'quiz_4'],
        practicalProjects: 3,
        capstoneProject: true,
      },
      validityPeriod: 1460, // days (4 years)
      issuingOrganization: 'Skillzen Academy',
      certificateDetails: {
        badgeDesign: 'fullstack-badge.svg',
        borderColor: '#ff6b6b',
        accentColor: '#4ecdc4',
        logoUrl: 'https://skillzen.com/logos/fullstack-cert.png',
      },
      totalIssued: 34,
      totalCompleted: 28,
      averageCompletionTime: 90, // days
      createdAt: '2024-09-15T07:30:00Z',
      updatedAt: '2024-11-28T16:20:00Z',
      createdBy: 'instructor@skillzen.com',
    },
    {
      id: 'cert_4',
      name: 'Data Science Fundamentals',
      description: 'Certificate in data science fundamentals including Python, pandas, visualization, and basic machine learning.',
      courseId: '4',
      courseName: 'Python for Data Science',
      status: 'draft',
      templateId: 'template_4',
      requirements: {
        courseCompletion: true,
        minimumScore: 75,
        requiredAssignments: ['assignment_8'],
        requiredQuizzes: ['quiz_5'],
        practicalProjects: 1,
      },
      validityPeriod: 1095, // days (3 years)
      issuingOrganization: 'Skillzen Academy',
      certificateDetails: {
        badgeDesign: 'datascience-badge.svg',
        borderColor: '#3776ab',
        accentColor: '#ffd43b',
        logoUrl: 'https://skillzen.com/logos/datascience-cert.png',
      },
      totalIssued: 0,
      totalCompleted: 0,
      averageCompletionTime: 35, // days
      createdAt: '2024-12-01T12:00:00Z',
      updatedAt: '2024-12-01T12:00:00Z',
      createdBy: 'instructor@skillzen.com',
    },
    {
      id: 'cert_5',
      name: 'Cloud Computing Essentials',
      description: 'Certificate covering cloud computing fundamentals, AWS services, and deployment strategies.',
      courseId: '5',
      courseName: 'Cloud Computing with AWS',
      status: 'active',
      templateId: 'template_5',
      requirements: {
        courseCompletion: true,
        minimumScore: 85,
        requiredAssignments: ['assignment_9'],
        requiredQuizzes: ['quiz_6', 'quiz_7'],
        practicalProjects: 2,
        awsCertificationPrep: true,
      },
      validityPeriod: 1095, // days (3 years)
      issuingOrganization: 'Skillzen Academy',
      certificateDetails: {
        badgeDesign: 'aws-badge.svg',
        borderColor: '#ff9900',
        accentColor: '#232f3e',
        logoUrl: 'https://skillzen.com/logos/aws-cert.png',
      },
      totalIssued: 67,
      totalCompleted: 59,
      averageCompletionTime: 52, // days
      createdAt: '2024-10-20T14:00:00Z',
      updatedAt: '2024-12-03T09:45:00Z',
      createdBy: 'instructor@skillzen.com',
    },
  ],

  templates: [
    {
      id: 'template_1',
      name: 'Modern Certificate Template',
      description: 'Clean, modern certificate design with customizable colors and branding',
      category: 'modern',
      designElements: {
        layout: 'landscape',
        headerHeight: '20%',
        footerHeight: '15%',
        backgroundColor: '#ffffff',
        borderWidth: 3,
        borderStyle: 'solid',
        fontFamily: 'Inter, sans-serif',
        titleFontSize: '32px',
        bodyFontSize: '16px',
        signatureFontSize: '14px',
      },
      placeholders: {
        recipientName: '{recipient_name}',
        certificationName: '{certification_name}',
        completionDate: '{completion_date}',
        issuingOrganization: '{organization_name}',
        certificateNumber: '{certificate_number}',
        signatoryName: '{signatory_name}',
        signatoryTitle: '{signatory_title}',
      },
      customFields: [
        { name: 'instructor', label: 'Instructor Name', type: 'text', required: false },
        { name: 'grade', label: 'Final Grade', type: 'text', required: false },
        { name: 'hours', label: 'Training Hours', type: 'number', required: false },
      ],
      usageCount: 4,
      isDefault: true,
      createdAt: '2024-09-01T00:00:00Z',
      updatedAt: '2024-11-15T12:00:00Z',
    },
    {
      id: 'template_2',
      name: 'Classic Academic Template',
      description: 'Traditional academic-style certificate with elegant typography and borders',
      category: 'academic',
      designElements: {
        layout: 'landscape',
        headerHeight: '25%',
        footerHeight: '20%',
        backgroundColor: '#fefefe',
        borderWidth: 2,
        borderStyle: 'double',
        fontFamily: 'Times New Roman, serif',
        titleFontSize: '28px',
        bodyFontSize: '18px',
        signatureFontSize: '16px',
      },
      placeholders: {
        recipientName: '{recipient_name}',
        certificationName: '{certification_name}',
        completionDate: '{completion_date}',
        issuingOrganization: '{organization_name}',
        certificateNumber: '{certificate_number}',
        signatoryName: '{signatory_name}',
        signatoryTitle: '{signatory_title}',
      },
      customFields: [
        { name: 'honors', label: 'Honors/Distinction', type: 'text', required: false },
        { name: 'gpa', label: 'GPA', type: 'number', required: false },
      ],
      usageCount: 2,
      isDefault: false,
      createdAt: '2024-09-15T00:00:00Z',
      updatedAt: '2024-10-20T14:30:00Z',
    },
    {
      id: 'template_3',
      name: 'Professional Badge Template',
      description: 'Professional certificate with digital badge integration and QR code verification',
      category: 'professional',
      designElements: {
        layout: 'portrait',
        headerHeight: '30%',
        footerHeight: '15%',
        backgroundColor: '#f8f9fa',
        borderWidth: 1,
        borderStyle: 'solid',
        fontFamily: 'Roboto, sans-serif',
        titleFontSize: '24px',
        bodyFontSize: '14px',
        signatureFontSize: '12px',
      },
      placeholders: {
        recipientName: '{recipient_name}',
        certificationName: '{certification_name}',
        completionDate: '{completion_date}',
        issuingOrganization: '{organization_name}',
        certificateNumber: '{certificate_number}',
        signatoryName: '{signatory_name}',
        signatoryTitle: '{signatory_title}',
      },
      customFields: [
        { name: 'skills', label: 'Skills Demonstrated', type: 'textarea', required: false },
        { name: 'projects', label: 'Projects Completed', type: 'number', required: false },
      ],
      usageCount: 1,
      isDefault: false,
      createdAt: '2024-10-01T00:00:00Z',
      updatedAt: '2024-11-01T10:15:00Z',
    },
    {
      id: 'template_4',
      name: 'Tech Certificate Template',
      description: 'Modern tech-focused certificate with gradient backgrounds and tech icons',
      category: 'technology',
      designElements: {
        layout: 'landscape',
        headerHeight: '22%',
        footerHeight: '18%',
        backgroundColor: '#1a1a1a',
        borderWidth: 0,
        borderStyle: 'none',
        fontFamily: 'JetBrains Mono, monospace',
        titleFontSize: '30px',
        bodyFontSize: '16px',
        signatureFontSize: '14px',
      },
      placeholders: {
        recipientName: '{recipient_name}',
        certificationName: '{certification_name}',
        completionDate: '{completion_date}',
        issuingOrganization: '{organization_name}',
        certificateNumber: '{certificate_number}',
        signatoryName: '{signatory_name}',
        signatoryTitle: '{signatory_title}',
      },
      customFields: [
        { name: 'technologies', label: 'Technologies Mastered', type: 'text', required: false },
        { name: 'version', label: 'Certification Version', type: 'text', required: false },
      ],
      usageCount: 1,
      isDefault: false,
      createdAt: '2024-11-01T00:00:00Z',
      updatedAt: '2024-11-01T00:00:00Z',
    },
    {
      id: 'template_5',
      name: 'Minimalist Template',
      description: 'Clean, minimalist certificate design focusing on content over decoration',
      category: 'minimalist',
      designElements: {
        layout: 'landscape',
        headerHeight: '15%',
        footerHeight: '10%',
        backgroundColor: '#ffffff',
        borderWidth: 1,
        borderStyle: 'solid',
        fontFamily: 'Lato, sans-serif',
        titleFontSize: '26px',
        bodyFontSize: '15px',
        signatureFontSize: '13px',
      },
      placeholders: {
        recipientName: '{recipient_name}',
        certificationName: '{certification_name}',
        completionDate: '{completion_date}',
        issuingOrganization: '{organization_name}',
        certificateNumber: '{certificate_number}',
        signatoryName: '{signatory_name}',
        signatoryTitle: '{signatory_title}',
      },
      customFields: [
        { name: 'duration', label: 'Course Duration', type: 'text', required: false },
      ],
      usageCount: 1,
      isDefault: false,
      createdAt: '2024-10-15T00:00:00Z',
      updatedAt: '2024-10-15T00:00:00Z',
    },
  ],

  issuedCertificates: [
    {
      id: 'issued_cert_1',
      certificationId: 'cert_1',
      certificationName: 'JavaScript Fundamentals Certificate',
      userId: '3',
      recipientName: 'Jane Student',
      recipientEmail: 'student@skillzen.com',
      certificateNumber: 'CERT-2024-JS-001',
      issuedDate: '2024-11-15T10:30:00Z',
      completedDate: '2024-11-14T16:45:00Z',
      expiryDate: '2026-11-15T10:30:00Z',
      status: 'active',
      finalScore: 87.5,
      completionTime: 26, // days
      downloadUrl: 'https://certificates.skillzen.com/cert-2024-js-001.pdf',
      verificationUrl: 'https://verify.skillzen.com/cert-2024-js-001',
      shareUrl: 'https://skillzen.com/certificates/share/cert-2024-js-001',
      blockchainHash: 'abc123def456ghi789jkl012mno345pqr678stu901vwx234',
      metadata: {
        templateUsed: 'template_1',
        issuerSignature: 'Dr. John Instructor',
        issuerTitle: 'Lead JavaScript Instructor',
        organizationSeal: 'skillzen-academy-seal.png',
      },
    },
    {
      id: 'issued_cert_2',
      certificationId: 'cert_2',
      certificationName: 'React Development Certificate',
      userId: '4',
      recipientName: 'Alice Johnson',
      recipientEmail: 'alice.johnson@example.com',
      certificateNumber: 'CERT-2024-REACT-001',
      issuedDate: '2024-12-01T14:20:00Z',
      completedDate: '2024-11-30T18:30:00Z',
      expiryDate: '2027-12-01T14:20:00Z',
      status: 'active',
      finalScore: 92.8,
      completionTime: 38, // days
      downloadUrl: 'https://certificates.skillzen.com/cert-2024-react-001.pdf',
      verificationUrl: 'https://verify.skillzen.com/cert-2024-react-001',
      shareUrl: 'https://skillzen.com/certificates/share/cert-2024-react-001',
      blockchainHash: 'xyz789uvw456rst123opq890lmn567ijk234fgh901cde678',
      metadata: {
        templateUsed: 'template_2',
        issuerSignature: 'Sarah React Expert',
        issuerTitle: 'Senior React Developer',
        organizationSeal: 'skillzen-academy-seal.png',
        projects: ['Todo App', 'E-commerce Dashboard'],
      },
    },
    {
      id: 'issued_cert_3',
      certificationId: 'cert_1',
      certificationName: 'JavaScript Fundamentals Certificate',
      userId: '5',
      recipientName: 'Bob Wilson',
      recipientEmail: 'bob.wilson@example.com',
      certificateNumber: 'CERT-2024-JS-002',
      issuedDate: '2024-11-20T09:15:00Z',
      completedDate: '2024-11-19T14:22:00Z',
      expiryDate: '2026-11-20T09:15:00Z',
      status: 'active',
      finalScore: 94.2,
      completionTime: 22, // days
      downloadUrl: 'https://certificates.skillzen.com/cert-2024-js-002.pdf',
      verificationUrl: 'https://verify.skillzen.com/cert-2024-js-002',
      shareUrl: 'https://skillzen.com/certificates/share/cert-2024-js-002',
      blockchainHash: 'mno345pqr678stu901vwx234yzb567cda890efg123hij456',
      metadata: {
        templateUsed: 'template_1',
        issuerSignature: 'Dr. John Instructor',
        issuerTitle: 'Lead JavaScript Instructor',
        organizationSeal: 'skillzen-academy-seal.png',
      },
    },
    {
      id: 'issued_cert_4',
      certificationId: 'cert_3',
      certificationName: 'Full Stack Developer Certificate',
      userId: '6',
      recipientName: 'Carol Martinez',
      recipientEmail: 'carol.martinez@example.com',
      certificateNumber: 'CERT-2024-FULLSTACK-001',
      issuedDate: '2024-10-15T11:45:00Z',
      completedDate: '2024-10-14T20:15:00Z',
      expiryDate: '2028-10-15T11:45:00Z',
      status: 'active',
      finalScore: 96.5,
      completionTime: 85, // days
      downloadUrl: 'https://certificates.skillzen.com/cert-2024-fullstack-001.pdf',
      verificationUrl: 'https://verify.skillzen.com/cert-2024-fullstack-001',
      shareUrl: 'https://skillzen.com/certificates/share/cert-2024-fullstack-001',
      blockchainHash: 'qwe789rty456uio123pas890dfg567hjk234lmn901bnm678',
      metadata: {
        templateUsed: 'template_3',
        issuerSignature: 'Michael Full Stack',
        issuerTitle: 'Senior Full Stack Architect',
        organizationSeal: 'skillzen-academy-seal.png',
        capstoneProject: 'Social Media Platform',
        technologies: ['React', 'Node.js', 'PostgreSQL', 'Docker'],
      },
    },
  ],

  progress: [
    {
      id: 'progress_1',
      certificationId: 'cert_1',
      userId: '7',
      userName: 'David Lee',
      userEmail: 'david.lee@example.com',
      status: 'in_progress',
      overallProgress: 75,
      requirements: {
        courseCompletion: {
          required: true,
          completed: true,
          completedAt: '2024-11-25T14:30:00Z',
        },
        minimumScore: {
          required: 80,
          currentScore: 78,
          achieved: false,
        },
        requiredAssignments: {
          required: ['assignment_1', 'assignment_2'],
          completed: ['assignment_1'],
          pending: ['assignment_2'],
        },
        requiredQuizzes: {
          required: ['quiz_1'],
          completed: ['quiz_1'],
          scores: { quiz_1: 82 },
        },
        practicalProjects: {
          required: 1,
          completed: 0,
          submitted: [],
        },
      },
      estimatedCompletion: '2024-12-20T00:00:00Z',
      createdAt: '2024-11-01T08:00:00Z',
      updatedAt: '2024-11-25T14:30:00Z',
    },
    {
      id: 'progress_2',
      certificationId: 'cert_2',
      userId: '8',
      userName: 'Emma Chen',
      userEmail: 'emma.chen@example.com',
      status: 'in_progress',
      overallProgress: 45,
      requirements: {
        courseCompletion: {
          required: true,
          completed: false,
          progress: 65,
        },
        minimumScore: {
          required: 85,
          currentScore: 0,
          achieved: false,
        },
        requiredAssignments: {
          required: ['assignment_3', 'assignment_4'],
          completed: [],
          pending: ['assignment_3', 'assignment_4'],
        },
        requiredQuizzes: {
          required: ['quiz_2'],
          completed: [],
          pending: ['quiz_2'],
        },
        practicalProjects: {
          required: 2,
          completed: 1,
          submitted: ['Personal Portfolio'],
        },
      },
      estimatedCompletion: '2025-01-15T00:00:00Z',
      createdAt: '2024-11-15T10:00:00Z',
      updatedAt: '2024-12-01T16:45:00Z',
    },
  ],

  analytics: [
    {
      certificationId: 'cert_1',
      totalEnrolled: 245,
      totalCompleted: 156,
      completionRate: 63.7,
      averageCompletionTime: 28,
      certificatesIssued: 156,
      certificatesActive: 154,
      certificatesRevoked: 2,
      averageScore: 86.4,
      monthlyStats: [
        { month: '2024-08', enrolled: 25, completed: 18, issued: 18 },
        { month: '2024-09', enrolled: 32, completed: 24, issued: 24 },
        { month: '2024-10', enrolled: 41, completed: 35, issued: 35 },
        { month: '2024-11', enrolled: 38, completed: 29, issued: 29 },
        { month: '2024-12', enrolled: 27, completed: 15, issued: 15 },
      ],
      topScores: [
        { userId: '5', name: 'Bob Wilson', score: 94.2 },
        { userId: '6', name: 'Carol Martinez', score: 92.8 },
        { userId: '3', name: 'Jane Student', score: 87.5 },
      ],
      difficultyBreakdown: {
        requirements: 85,
        quizzes: 75,
        assignments: 90,
        projects: 70,
      },
    },
    {
      certificationId: 'cert_2',
      totalEnrolled: 145,
      totalCompleted: 89,
      completionRate: 61.4,
      averageCompletionTime: 42,
      certificatesIssued: 89,
      certificatesActive: 87,
      certificatesRevoked: 2,
      averageScore: 88.7,
      monthlyStats: [
        { month: '2024-08', enrolled: 18, completed: 12, issued: 12 },
        { month: '2024-09', enrolled: 22, completed: 16, issued: 16 },
        { month: '2024-10', enrolled: 28, completed: 21, issued: 21 },
        { month: '2024-11', enrolled: 35, completed: 22, issued: 22 },
        { month: '2024-12', enrolled: 19, completed: 8, issued: 8 },
      ],
      topScores: [
        { userId: '4', name: 'Alice Johnson', score: 96.5 },
        { userId: '9', name: 'Frank Rodriguez', score: 91.2 },
        { userId: '10', name: 'Grace Kim', score: 89.8 },
      ],
      difficultyBreakdown: {
        requirements: 90,
        quizzes: 80,
        assignments: 85,
        projects: 75,
      },
    },
  ],

  categories: [
    {
      id: 'programming',
      name: 'Programming',
      description: 'Programming languages and software development',
      count: 2,
      color: '#3b82f6',
    },
    {
      id: 'web-development',
      name: 'Web Development',
      description: 'Frontend and backend web technologies',
      count: 3,
      color: '#22c55e',
    },
    {
      id: 'data-science',
      name: 'Data Science',
      description: 'Data analysis, machine learning, and statistics',
      count: 1,
      color: '#8b5cf6',
    },
    {
      id: 'cloud-computing',
      name: 'Cloud Computing',
      description: 'Cloud platforms and DevOps practices',
      count: 1,
      color: '#f59e0b',
    },
    {
      id: 'cybersecurity',
      name: 'Cybersecurity',
      description: 'Information security and ethical hacking',
      count: 0,
      color: '#ef4444',
    },
  ],
};
